"use client";

import { useEffect, useState } from "react";

import { ClientErrorBoundary } from "@/components/error/ClientErrorBoundary";
import { ResponsiveDialog } from "@/components/responsive-dialog";

import { transactionDetailComponents } from "./details";
import LedgerEntryListBasicInfo from "./ledger-entry-list-basic-info";
import LedgerEntryListBasicInfoCombined from "./ledger-entry-list-basic-info-combined";
import { JournalType } from "./types";

interface LedgerEntryDrawerDetailsProps {
  entry: LedgerEntryOrCombined | null;
  className?: string;
  onClose: () => void;
  preventClose?: boolean;
}

// Type guard to check if entry is a combined FX entry
const isCombinedEntry = (
  entry: LedgerEntryOrCombined | null
): entry is CombinedFxEntry => {
  return entry !== null && "isCombined" in entry && entry.isCombined === true;
};

export default function LedgerEntryDrawerDetails({
  entry,
  className,
  onClose,
  preventClose = false,
}: LedgerEntryDrawerDetailsProps) {
  // Store the entry locally to maintain it during closing animation
  const [localEntry, setLocalEntry] = useState<LedgerEntryOrCombined | null>(
    null
  );

  // Update local entry when prop changes (but only when opening)
  useEffect(() => {
    if (entry) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Preserve entry during close animation
      setLocalEntry(entry);
    }
  }, [entry]);

  // Clear local entry after closing animation completes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      // Delay clearing the entry to allow closing animation to complete
      // Make sure the delay is the same as the CSS transition duration
      setTimeout(() => {
        setLocalEntry(null);
      }, 300);
    }
  };

  const renderTransactionDetails = () => {
    if (!localEntry) return null;

    // For combined entries, use the credit entry for details
    const entryForDetails = isCombinedEntry(localEntry)
      ? localEntry.creditEntry
      : localEntry;

    const journal = entryForDetails.journal as JournalType;
    const DetailComponent = transactionDetailComponents[journal];

    if (!DetailComponent) return null;

    // Use any to bypass TypeScript's inability to narrow union types in this context
    // This is simpler than using a switch statement
    return (
      <ClientErrorBoundary>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <DetailComponent entry={entryForDetails as any} />
      </ClientErrorBoundary>
    );
  };

  // Render appropriate header based on entry type
  const title = isCombinedEntry(localEntry) ? (
    <LedgerEntryListBasicInfoCombined entry={localEntry} isHeader />
  ) : (
    <LedgerEntryListBasicInfo
      showSubtitle={false}
      truncate
      isHeader
      entry={localEntry}
    />
  );

  return (
    <ResponsiveDialog
      open={!!entry}
      onOpenChange={handleOpenChange}
      title={title}
      className={className}
      preventClose={preventClose}
    >
      <div className="mx-3 lg:mx-0">
        {localEntry && <div>{renderTransactionDetails()}</div>}
      </div>
    </ResponsiveDialog>
  );
}
